



























































































































import Chart from "chart.js";
import Component from "vue-class-component";

import CoreApi from "@/utils/core-api";
import * as modals from "@/utils/modals";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import ConnectionHistoryChart, { ChartInfo } from "./ConnectionHistoryChart";
import { ConnectionHistoryChartInfo } from "@/models";
import { BaseRootPageComponent } from "../BaseRootPageComponent";

@Component({
  components: {
    LoadingIndicator,
    ConnectionHistoryChart
  }
})
export default class ConnectionHistory extends BaseRootPageComponent {
  loading = true;
  numberOfDays = 7;
  from!: Date;
  to!: Date;
  charts: ChartInfo[] = [];

  /**
   * Used to track the last horizontal scroll position of the chart container so that vertical scrolling can be ignored
   */
  lastChartContainerScrollLeft?: number;
  /** Debouce timeout used to prevent too much processing when the window is resizing */
  resizeTimeout?: ReturnType<typeof setTimeout>;
  mouseMoveTiemout?: ReturnType<typeof setTimeout>;

  async created() {
    this.setTitle("Connection History");

    window.addEventListener("resize", this.handleResize);

    this.setDateRange();
    await this.loadHistory();
    this.loading = false;

    // In case the data isn't loaded before mounting, set the container's size. Use setTimeout so that it will run
    // after the UI has been updated and the element has been created
    setTimeout(this.initalize);
  }

  mounted() {
    this.initalize();
  }

  initalize() {
    this.setContainerSize();

    // Scroll to the right of the graphs so the user can see the recent info first
    const container = this.$refs.chartContainer as HTMLElement | undefined;
    if (!container) return;

    // Before the window can be scrolled to the right, scroll 1px and call handleContainerScrolled to float
    // the serials container (which has a side-effect of adjusting the width). After, scroll the entire width
    container.scrollLeft = 1;
    this.handleContainerScrolled();
    container.scrollLeft = container.scrollWidth;
  }

  destory() {
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
  }

  handleResize() {
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);

    const debounce = 100;
    this.resizeTimeout = setTimeout(this.setContainerSize, debounce);
  }

  setContainerSize() {
    const container = this.$refs.chartContainer as HTMLElement | undefined;
    if (!container) return;

    const bounds = container.getBoundingClientRect();
    container.style.maxHeight = "calc(" + (window.innerHeight - bounds.top) + "px - 1.5rem)";
  }

  // When the user scrolls the chart container, float the serials on top of the charts so that they are always visible
  // while the user is scrolling horizontally through the charts
  handleContainerScrolled() {
    const container = this.$refs.chartContainer as HTMLElement | undefined;
    const serials = this.$refs.serialContainer as HTMLElement | undefined;
    if (!container || !serials) return;

    // Only recalculate if there was a change (e.g. ignore vertical scrolling)
    if (container.scrollLeft === this.lastChartContainerScrollLeft) return;

    // Hide the tooltip when scrolling (otherwise the tooltip's value is out of date)
    const tooltip = this.$refs.tooltip as HTMLElement | undefined;
    const verticalLine = this.$refs.verticalLine as HTMLElement | undefined;
    if (tooltip) tooltip.style.display = "none";
    if (verticalLine) verticalLine.style.display = "none";

    if (container.scrollLeft === 0) {
      serials.style.position = "static";
      serials.style.left = "";
      container.style.paddingLeft = "0";
    } else {
      serials.style.position = serials.style.position = "absolute";
      serials.style.left = container.scrollLeft + "px";
      container.style.paddingLeft = serials.clientWidth + "px";
    }

    this.lastChartContainerScrollLeft = container.scrollLeft;
  }

  handleContainerMouseMoved(event: MouseEvent) {
    if (this.mouseMoveTiemout) clearTimeout(this.mouseMoveTiemout);

    const debounce = 100;
    this.resizeTimeout = setTimeout(() => {
      const container = this.$refs.chartContainer as HTMLElement | undefined;
      const serials = this.$refs.serialContainer as HTMLElement | undefined;
      const tooltip = this.$refs.tooltip as HTMLElement | undefined;
      const verticalLine = this.$refs.verticalLine as HTMLElement | undefined;
      if (!tooltip || !container || !serials || !verticalLine) return;

      // If the user is hovering over the serials container, don't show the tooltip
      const serialsLeft = container.offsetLeft + serials.scrollLeft;
      if (event.pageX >= serialsLeft && event.pageX <= serialsLeft + serials.scrollWidth) {
        tooltip.style.display = "none";
        verticalLine.style.display = "none";
        return;
      }

      // If there isn't a time available, don't show the tooltip
      const time = this.getTimeForPixel(event.offsetX);
      if (!time) {
        tooltip.style.display = "none";
        verticalLine.style.display = "none";
        return;
      }

      tooltip.style.display = "inline-flex";
      tooltip.style.left = event.pageX - tooltip.clientWidth / 2 + "px";
      tooltip.style.top = event.pageY - tooltip.clientHeight - 10 + "px";

      verticalLine.style.display = "block";
      verticalLine.style.left = container.scrollLeft + event.pageX - container.offsetLeft + "px";
      verticalLine.style.height = container.scrollHeight + "px";

      const content = tooltip.getElementsByClassName("tooltip-content")[0] as HTMLElement;
      content.innerText = time;
    }, debounce);
  }

  getTimeForPixel(x: number): string | null {
    const chartIds = Object.getOwnPropertyNames(Chart.instances);
    if (chartIds.length === 0) return null;

    const chart = Chart.instances[chartIds[0]] as any;
    return chart.scales["x-axis-0"].getValueForPixel(x).format("ll LTS");
  }

  setDateRange() {
    // Start with the current date
    this.to = new Date();

    // Make from equal to the start of the day (no timestamp), then go back the request number of days
    this.from = new Date(this.to.getFullYear(), this.to.getMonth(), this.to.getDate());
    this.from.setDate(this.from.getDate() - this.numberOfDays);
  }

  async loadHistory() {
    try {
      const records = await CoreApi.getConnectionHistory(this.from.toISOString(), this.to.toISOString());

      this.charts = records.map(this.buildChart);
    } catch (error) {
      if ((error as any).isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else {
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }
    }
  }

  buildChart(info: ConnectionHistoryChartInfo): ChartInfo {
    const thresholdInMinutes = 1; // The keep alive updates every 30 seconds, so use double that to

    let lastIp = "";
    const connectedPoints: Chart.ChartPoint[] = [];
    const disconnectedPoints: Chart.ChartPoint[] = [];

    // If there aren't any records, draw a line from the start until the end as disconnected
    if (info.records.length === 0) {
      return {
        serial: info.serial,
        lastIp: lastIp,
        connectedPoints: [],
        disconnectedPoints: [
          { x: this.from, y: 0 },
          { x: this.to, y: 0 }
        ]
      };
    }

    info.records.forEach((r, i) => {
      if (i === 0) {
        if (this.getDateDiffInMinutes(this.from, new Date(r.timeStarted)) > thresholdInMinutes) {
          disconnectedPoints.push({ x: this.from, y: 0 });
          disconnectedPoints.push({ x: r.timeStarted, y: 0 });
        }
      }

      lastIp = r.ip;

      // Create a new segment for the connection
      connectedPoints.push({ x: r.timeStarted, y: NaN });
      connectedPoints.push({ x: r.timeStarted, y: 0 });
      connectedPoints.push({ x: r.timeStarted, y: 1 });
      connectedPoints.push({ x: r.timeLastSeen, y: 1 });

      // If this is the last point, don't treat the vent as disconnected since it may still be connected
      const isLastVent = i === info.records.length - 1;
      const isVentStillConnected =
        isLastVent && this.getDateDiffInMinutes(new Date(r.timeLastSeen), this.to) <= thresholdInMinutes;
      if (isVentStillConnected) return;

      // Create a new segment for the disconection
      const nextTime = isLastVent ? this.to : info.records[i + 1].timeStarted;
      disconnectedPoints.push({ x: r.timeLastSeen, y: NaN });
      disconnectedPoints.push({ x: r.timeLastSeen, y: 1 });
      disconnectedPoints.push({ x: r.timeLastSeen, y: 0 });
      disconnectedPoints.push({ x: nextTime, y: 0 });
    });

    return {
      serial: info.serial,
      lastIp: lastIp,
      connectedPoints,
      disconnectedPoints
    };
  }

  getDateDiffInMinutes(date1: Date, date2: Date) {
    return (date2.getTime() - date1.getTime()) / (60 * 1000);
  }
}
